import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { MapPin, User } from "lucide-react";
import { getNewestPilots, REGISTER_URL } from "@/lib/api";

const initials = (name) =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const PilotCard = ({ pilot }) => (
  <div
    data-testid={`pilot-card-${pilot.callsign}`}
    className="mx-3 w-72 flex-shrink-0 rounded-2xl border border-white/5 bg-[var(--aiv-surface)] p-6 transition-colors duration-300 hover:border-[var(--aiv-accent)]/30"
  >
    <div className="flex items-center gap-4">
      {pilot.avatar ? (
        <img src={pilot.avatar} alt={pilot.name} className="h-14 w-14 rounded-full object-cover" />
      ) : (
        <span className="flex h-14 w-14 items-center justify-center rounded-full border border-[var(--aiv-accent)]/30 bg-[#8A1538]/20 font-mono-data text-sm font-bold text-[var(--aiv-accent)]">
          {initials(pilot.name) || <User className="h-5 w-5" />}
        </span>
      )}
      <div className="min-w-0">
        <p className="truncate text-base font-semibold text-[var(--aiv-text)]">{pilot.name}</p>
        <p className="font-mono-data text-xs text-[var(--aiv-text-2)]">{pilot.callsign}</p>
      </div>
    </div>
    <div className="mt-5 flex items-start gap-2 text-sm text-[var(--aiv-text-2)]">
      <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#8A1538]" />
      <span className="line-clamp-2">{pilot.location}</span>
    </div>
    <p className="mt-3 text-xs text-[var(--aiv-text-2)]/70">Joined {pilot.joined}</p>
  </div>
);

export const NewestPilots = () => {
  const [pilots, setPilots] = useState([]);

  useEffect(() => {
    getNewestPilots().then(setPilots).catch(() => {});
  }, []);

  return (
    <section id="pilots" data-testid="pilots-section" className="relative overflow-hidden py-24 lg:py-32">
      <div className="mx-auto mb-12 max-w-7xl px-5 lg:px-10">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-xl">
            <p className="text-overline text-[#8A1538]">Fresh recruits</p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-[var(--aiv-text)] lg:text-4xl">
              Our Newest Pilots
            </h2>
            <p className="mt-4 text-base text-[var(--aiv-text-2)]">
              Welcome to the crew. New aviators joining the ranks every week from
              airports across the globe.
            </p>
          </div>
          <Link
            to={REGISTER_URL}
            data-testid="pilots-join-btn"
            className="rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-[var(--aiv-text)] transition-colors hover:border-[var(--aiv-accent)]/50 hover:text-[var(--aiv-accent)]"
          >
            Join the roster
          </Link>
        </div>
      </div>

      {pilots.length > 0 && (
        <Marquee gradient={false} speed={40} pauseOnHover className="py-2">
          {pilots.map((p) => (
            <PilotCard key={p.callsign} pilot={p} />
          ))}
        </Marquee>
      )}
    </section>
  );
};
